import { useEffect, useRef, useState } from "react";
import { DatabaseBackup, Download, Upload, ShieldAlert, History, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fecha } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const CONFIRMACION = "RESTAURAR TODO";
const mb = (bytes) => `${(Number(bytes || 0) / 1_048_576).toFixed(2)} MB`;

/** Respaldo, restauración y bitácora: pantalla exclusiva del SuperAdmin. */
export default function AdminSistema() {
  const [resumen, setResumen] = useState(null);
  const [estado, setEstado] = useState(null);
  const [bitacora, setBitacora] = useState([]);
  const [descargando, setDescargando] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const [confirmacion, setConfirmacion] = useState("");
  const [archivoElegido, setArchivoElegido] = useState("");
  const archivo = useRef(null);
  const listoParaRestaurar = confirmacion === CONFIRMACION && !!archivoElegido;

  const cargar = () => {
    api.get("/backup/resumen").then((r) => setResumen(r.data)).catch(() => setResumen({ colecciones: [] }));
    api.get("/sistema/estado").then((r) => setEstado(r.data)).catch(() => setEstado(null));
    api.get("/backup/bitacora").then((r) => setBitacora(r.data)).catch(() => setBitacora([]));
  };

  useEffect(cargar, []);

  const descargar = async () => {
    setDescargando(true);
    try {
      const { data } = await api.get("/backup/descargar", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `respaldo-sis-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 8000);
      toast.success("Respaldo descargado", { description: "Guárdelo en un lugar seguro." });
      cargar();
    } catch (e) {
      toast.error(apiError(e, "No se pudo generar el respaldo"));
    }
    setDescargando(false);
  };

  const restaurar = async () => {
    const file = archivo.current?.files?.[0];
    if (!file) return toast.error("Elija el archivo .zip del respaldo");
    if (confirmacion !== CONFIRMACION) {
      return toast.error(`Escriba exactamente "${CONFIRMACION}" para confirmar`);
    }
    if (!window.confirm("Esto reemplaza TODOS los datos actuales por los del respaldo. ¿Continuar?")) {
      return;
    }
    setRestaurando(true);
    const cuerpo = new FormData();
    cuerpo.append("archivo", file);
    cuerpo.append("confirmacion", confirmacion.trim());
    try {
      const { data } = await api.post("/backup/restaurar", cuerpo,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sistema restaurado", {
        description: `${data.registros} registros en ${Object.keys(data.colecciones).length} colecciones.`,
      });
      setConfirmacion("");
      setArchivoElegido("");
      if (archivo.current) archivo.current.value = "";
      cargar();
    } catch (e) {
      toast.error(apiError(e, "No se pudo restaurar el respaldo"));
    }
    setRestaurando(false);
  };

  if (!resumen) return <Loading />;

  return (
    <div data-testid="admin-sistema">
      <PageTitle title="Respaldo y sistema"
                 subtitle="Descargue una copia completa del sistema, restaure un respaldo y revise la bitácora. Pantalla exclusiva del SuperAdmin." />

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Registros en la base" value={resumen.registros || 0} icon={DatabaseBackup}
              testId="sistema-registros" />
        <Stat label="Tamaño aproximado" value={mb(resumen.bytes)} icon={DatabaseBackup}
              testId="sistema-tamano" />
        <Stat label="Cuentas del personal"
              value={(estado?.roles?.superadmin || 0) + (estado?.roles?.admin || 0)
                     + (estado?.roles?.secretaria || 0) + (estado?.roles?.coordinadora || 0)
                     + (estado?.roles?.docente || 0)}
              icon={Users} testId="sistema-cuentas" />
        <Stat label="Último respaldo"
              value={resumen.ultimo_respaldo ? fecha(resumen.ultimo_respaldo.creado_en) : "nunca"}
              icon={History} tone={resumen.ultimo_respaldo ? "default" : "warn"}
              testId="sistema-ultimo-respaldo" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-border/60 bg-card p-6">
          <h2 className="font-display text-base font-semibold">Descargar respaldo</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Un archivo ZIP con todas las colecciones del sistema (estudiantes, familias, facturas,
            pagos, nómina, ponches, inventario, configuración y archivos adjuntos).
          </p>
          <Button className="mt-5 w-full rounded-full py-6" onClick={descargar}
                  disabled={descargando} data-testid="descargar-respaldo">
            <Download className="mr-2 h-4 w-4" />
            {descargando ? "Generando el respaldo…" : "Descargar respaldo completo"}
          </Button>
          <div className="mt-5 max-h-64 overflow-y-auto rounded-md border border-border/60"
               data-testid="tabla-colecciones">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Colección</th>
                  <th className="px-4 py-2">Registros</th>
                  <th className="px-4 py-2">Tamaño</th>
                </tr>
              </thead>
              <tbody>
                {resumen.colecciones.map((c) => (
                  <tr key={c.coleccion} className="border-t border-border/60">
                    <td className="px-4 py-2">{c.coleccion}</td>
                    <td className="px-4 py-2 font-mono text-xs">{c.registros}</td>
                    <td className="px-4 py-2 font-mono text-xs">{mb(c.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-6">
          <h2 className="flex items-center gap-2 font-display text-base font-semibold">
            <ShieldAlert className="h-4 w-4 text-destructive" /> Restaurar el sistema
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Reemplaza <b>todos</b> los datos actuales por los del archivo de respaldo. Úselo solo si
            necesita volver a un estado anterior; primero descargue un respaldo del estado actual.
          </p>
          <div className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label>Archivo de respaldo (.zip)</Label>
              <Input type="file" accept=".zip" ref={archivo} data-testid="archivo-respaldo"
                     onChange={(e) => setArchivoElegido(e.target.files?.[0]?.name || "")} />
            </div>
            <div className="space-y-2">
              <Label>Escriba «{CONFIRMACION}» para habilitar</Label>
              <Input value={confirmacion} className="font-mono" data-testid="confirmacion-restaurar"
                     placeholder={CONFIRMACION}
                     onChange={(e) => setConfirmacion(e.target.value)} />
              {!listoParaRestaurar && (
                <p className="text-xs text-muted-foreground">
                  {archivoElegido
                    ? "Falta escribir la frase exactamente en mayúsculas."
                    : "Elija el archivo del respaldo y escriba la frase exactamente en mayúsculas."}
                </p>
              )}
            </div>
            <Button variant="destructive" className="w-full rounded-full py-6"
                    disabled={restaurando || !listoParaRestaurar}
                    onClick={restaurar} data-testid="restaurar-respaldo">
              <Upload className="mr-2 h-4 w-4" />
              {restaurando ? "Restaurando…" : "Restaurar desde el respaldo"}
            </Button>
          </div>
        </section>
      </div>

      <section className="mt-8">
        <h2 className="mb-3 font-display text-base font-semibold">Bitácora del sistema</h2>
        <div className="overflow-hidden rounded-lg border border-border/60 bg-card"
             data-testid="tabla-bitacora">
          {bitacora.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4 text-sm last:border-0">
              <Badge variant={b.accion === "restauracion" ? "destructive" : "secondary"}>
                {b.accion}
              </Badge>
              <span className="min-w-0 flex-1">
                <span className="block">{b.detalle}</span>
                <span className="block text-xs text-muted-foreground">
                  {b.usuario} · {b.rol} · {fecha(b.creado_en)}
                  {b.ip ? ` · ${b.ip}` : ""}
                </span>
              </span>
            </div>
          ))}
          {bitacora.length === 0 && (
            <div className="p-6"><Empty testId="bitacora-vacia">Sin movimientos registrados.</Empty></div>
          )}
        </div>
      </section>
    </div>
  );
}
